import { ref } from "vue";

import { apiClient } from "@/client";
import type { RMOMaintenanceStatus } from "@/type/api";

// RMO maintenance status fetch + poll (design doc/design/maintenance-mode.md §8.3-§8.4).
// The RMO SPA checks GET /api/rmo/maintenance at boot so App.vue can render the
// friendly branded takeover while the rmo gate is armed, and polls (~10s) while
// the gate stays active so the takeover clears itself once an admin lifts the
// gate — no manual reload required.
//
// A failed status fetch (network error, or the status endpoint itself 503ing
// when the gate whitelist breaks) is NOT proof the gate is armed, so it never
// triggers a takeover by itself: we keep the last observed state and let the
// next poll retry.

const POLL_INTERVAL_MS = 10_000;

const active = ref(false);
const message = ref<string | null>(null);
const startedAt = ref<string | null>(null);
const loading = ref(false);
// True once the boot status fetch has settled (success or failure), so App.vue
// can stop showing its neutral loading state and render content or takeover.
const checked = ref(false);

// Monotonic fetch id: a slow earlier response must never clobber a newer one.
let fetchSeq = 0;
let pollTimer: number | null = null;

function applyStatus(status: RMOMaintenanceStatus): void {
	active.value = status.active;
	message.value = status.message;
	startedAt.value = status.startedAt;
}

async function refresh(): Promise<void> {
	const seq = ++fetchSeq;
	loading.value = true;
	try {
		const status = await apiClient.JSONGet<RMOMaintenanceStatus>(
			"/api/rmo/maintenance",
		);
		if (seq !== fetchSeq) {
			return;
		}
		applyStatus(status);
	} catch {
		// 503 / network error from the status endpoint alone does not mean the
		// rmo gate is armed; keep the previous state and retry on the next poll.
		if (seq !== fetchSeq) {
			return;
		}
	} finally {
		if (seq === fetchSeq) {
			loading.value = false;
			checked.value = true;
		}
	}
}

function startPolling(intervalMs: number = POLL_INTERVAL_MS): void {
	if (pollTimer !== null) {
		return;
	}
	pollTimer = window.setInterval(() => {
		void refresh();
	}, intervalMs);
}

function stopPolling(): void {
	if (pollTimer !== null) {
		window.clearInterval(pollTimer);
		pollTimer = null;
	}
}

export function useRMOMaintenance() {
	return {
		// State
		active,
		checked,
		loading,
		message,
		startedAt,
		// Actions
		refresh,
		startPolling,
		stopPolling,
	};
}
